import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input, Label } from '@/components/ui/Form';
import {
  BirthDatePicker,
  BirthTimePicker,
  SegmentedControl,
  SelectField,
} from '@/components/ui/PremiumControls';
import { useAuthStore } from '@/lib/auth.store';
import { listMyCharts } from '@/lib/chart.api';
import { fetchPaywall } from '@/lib/business.api';
import { compatibilityApi, type CompatibilityReportSummary } from '@/lib/stage4.api';
import {
  ReportPaper,
  PremiumReportRenderer,
  parseReportSegments,
} from '@/components/premium/ReportPaper';
import { AnalyzingOverlay } from '@/components/premium/AnalyzingOverlay';
import { ReportPaywallModal } from '@/components/paywall/ReportPaywallModal';
import type { PaywallConfigView } from '@guanwei/shared';

type Mode = 'pick' | 'inline';
type Relation = 'couple' | 'partner' | 'family' | 'friend';
type GenderValue = 'male' | 'female' | 'unknown';

interface ChartItem {
  id: string;
  displayName: string;
  baziKey: string;
  gender: string;
  createdAt: string;
}

function immersiveShellClass(historyCollapsed: boolean) {
  return `mobile-immersive-shell relative mx-auto grid h-full min-h-0 w-full max-w-[1680px] overflow-hidden rounded-2xl border border-border bg-bg-1 shadow-gold lg:rounded-3xl ${
    historyCollapsed
      ? 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[0_minmax(0,1fr)]'
      : 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[360px_minmax(0,1fr)]'
  }`;
}

function immersiveHistoryClass(historyCollapsed: boolean) {
  return `mobile-immersive-history absolute inset-y-0 left-0 z-30 h-full w-[min(82vw,360px)] min-h-0 overflow-y-auto border-r border-border bg-bg p-4 shadow-gold transition-transform duration-base lg:relative lg:z-auto lg:w-auto lg:shadow-none lg:p-8 ${
    historyCollapsed
      ? '-translate-x-full lg:invisible lg:p-0 lg:opacity-0'
      : 'translate-x-0 lg:visible lg:opacity-100'
  }`;
}

function HistoryToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 top-3 z-40 inline-flex items-center gap-2 rounded-full border border-border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-fg shadow-gold transition-all hover:bg-accent-hover active:bg-accent-active lg:left-4 lg:top-4"
      aria-label={collapsed ? '查看历史记录' : '隐藏历史记录'}
    >
      <span aria-hidden="true">☰</span>
      <span>{collapsed ? '查看历史' : '隐藏历史'}</span>
    </button>
  );
}

function formatChartOptionDescription(chart: ChartItem) {
  const gender = chart.gender === 'male' ? '男' : chart.gender === 'female' ? '女' : '未指定';
  return `${gender} · ${chart.baziKey}`;
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    pending: '等待中',
    streaming: '生成中',
    completed: '已完成',
    failed: '失败',
    cast: '已成卦',
    interpreted: '已解读',
  };
  return map[status] ?? status;
}

export function CompatibilityPage() {
  const user = useAuthStore((s) => s.user);
  const [charts, setCharts] = useState<ChartItem[]>([]);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [historyCollapsed, setHistoryCollapsed] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 1024,
  );
  const [chartIdA, setChartIdA] = useState<string>('');
  const [chartIdB, setChartIdB] = useState<string>('');
  const [mode, setMode] = useState<Mode>('pick');
  const [partnerDate, setPartnerDate] = useState('');
  const [partnerTime, setPartnerTime] = useState('');
  const [partnerGender, setPartnerGender] = useState<GenderValue>('female');
  const [partnerPlace, setPartnerPlace] = useState('');
  const [nameA, setNameA] = useState('');
  const [nameB, setNameB] = useState('');
  const [relation, setRelation] = useState<Relation>('couple');
  const [streaming, setStreaming] = useState(false);
  const [content, setContent] = useState('');
  const [reportId, setReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paywallConfig, setPaywallConfig] = useState<PaywallConfigView | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [history, setHistory] = useState<CompatibilityReportSummary[]>([]);
  const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    void fetchPaywall('compatibility.preview')
      .then(setPaywallConfig)
      .catch(() => setPaywallConfig(null));
    void compatibilityApi
      .listMine()
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    if (!user) return;
    void listMyCharts()
      .then((list) => {
        setCharts(list);
        if (list.length > 0) setChartIdA(list[0].id);
      })
      .catch(() => setCharts([]))
      .finally(() => setLoadingCharts(false));
  }, [user]);

  const start = async () => {
    if (!chartIdA) {
      setError('请选择甲方命盘');
      return;
    }
    if (mode === 'pick' && !chartIdB) {
      setError('请选择乙方命盘或切换到「临时输入」');
      return;
    }
    if (mode === 'inline' && !partnerDate) {
      setError('请填写乙方出生日期');
      return;
    }
    setError(null);
    setContent('');
    setReportId(null);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    await compatibilityApi.stream(
      {
        chartIdA,
        chartIdB: mode === 'pick' ? chartIdB : undefined,
        partner:
          mode === 'inline'
            ? {
                date: partnerDate,
                time: partnerTime || undefined,
                gender: partnerGender,
                birthplace: partnerPlace || undefined,
              }
            : undefined,
        nameA: nameA || undefined,
        nameB: nameB || undefined,
        relation,
      },
      {
        onDelta: (delta) => setContent((s) => s + delta),
        onDone: (id) => {
          setStreaming(false);
          if (id) setReportId(id);
          void compatibilityApi
            .listMine()
            .then(setHistory)
            .catch(() => undefined);
        },
        onError: (err) => {
          if (err.message.includes('PAYWALL_REQUIRED')) {
            setPaywallOpen(true);
            setError('该报告需要购买或使用权益后生成');
          } else {
            setError(err.message);
          }
          setStreaming(false);
        },
      },
      controller.signal,
    );
  };

  const stop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  const openHistory = async (id: string) => {
    setLoadingHistoryId(id);
    setError(null);
    try {
      const detail = await compatibilityApi.detail(id);
      setContent(detail.content ?? '');
      setReportId(detail.id);
      setRelation(detail.relation);
      setNameA(detail.nameA ?? '');
      setNameB(detail.nameB ?? '');
      setStreaming(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingHistoryId(null);
    }
  };

  const shellClass = useMemo(() => immersiveShellClass(historyCollapsed), [historyCollapsed]);
  const historyClass = useMemo(() => immersiveHistoryClass(historyCollapsed), [historyCollapsed]);

  const historyPanel = (
    <aside className={historyClass}>
      <div className="mb-5">
        <p className="text-xs uppercase tracking-[0.28em] text-accent">History</p>
        <h2 className="mt-2 font-serif text-2xl text-fg">历史合盘</h2>
      </div>
      {history.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-bg-2 p-5 text-sm leading-7 text-fg-muted">
          暂无关系合盘报告。
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => void openHistory(item.id)}
              className="w-full rounded-2xl border border-border bg-bg-1 p-4 text-left transition-all hover:border-accent hover:bg-bg-2"
            >
              <p className="font-serif text-base text-fg">
                {item.nameA || '甲方'} × {item.nameB || '乙方'}
              </p>
              <p className="mt-1 text-xs text-fg-muted">
                {new Date(item.createdAt).toLocaleString()} ·{' '}
                {loadingHistoryId === item.id ? '读取中' : statusLabel(item.status)}
              </p>
            </button>
          ))}
        </div>
      )}
    </aside>
  );

  if (!user) {
    return (
      <main className="flex-1 flex items-center justify-center px-6">
        <Card className="p-10 text-center max-w-md">
          <p className="font-serif text-2xl text-fg mb-3">关系合盘</p>
          <p className="text-sm text-fg-2 mb-6">需要登录后才能使用——你的命盘信息严格加密</p>
          <Link to="/auth/login">
            <Button size="lg">登录</Button>
          </Link>
        </Card>
      </main>
    );
  }

  if (loadingCharts) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 text-sm text-fg-muted">
        正在读取命盘…
      </main>
    );
  }

  if (charts.length === 0) {
    return (
      <main className="flex-1 flex items-center justify-center px-6">
        <Card className="max-w-md p-10 text-center">
          <p className="mb-3 font-serif text-2xl text-fg">关系合盘</p>
          <p className="mb-6 text-sm leading-7 text-fg-2">
            关系合盘至少需要先绑定你的命盘，请先完成排盘后再进行分析。
          </p>
          <Link to="/chart">
            <Button size="lg">先去排盘</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="mobile-immersive-page h-full min-h-0 overflow-hidden px-3 py-3 sm:px-6 lg:px-8 lg:py-6">
      <div className={shellClass}>
        {historyPanel}
        <section className="mobile-immersive-content relative order-2 h-full min-h-0 space-y-6 overflow-y-auto overscroll-contain bg-bg-1 p-4 sm:p-8 lg:space-y-8 lg:p-10">
          <HistoryToggle
            collapsed={historyCollapsed}
            onClick={() => setHistoryCollapsed((value) => !value)}
          />
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-serif text-3xl text-fg">关系合盘</h1>
            <p className="text-sm text-fg-muted mt-2">
              从命盘动力学拆解两人关系的契合面、张力点与节奏，输出双方的行动建议
            </p>
          </motion.div>

          <Card className="p-6 space-y-6">
            <SegmentedControl
              label="关系类型"
              value={relation}
              onChange={setRelation}
              options={RELATION_OPTIONS}
            />

            {/* 甲方 */}
            <div>
              <p className="mb-2 font-serif text-lg text-fg">甲方 · 你的命盘</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <SelectField
                  label="选择命盘"
                  value={chartIdA}
                  onChange={setChartIdA}
                  placeholder="选择你的命盘"
                  options={charts.map((c) => ({
                    value: c.id,
                    label: c.displayName,
                    description: formatChartOptionDescription(c),
                  }))}
                />
                <div>
                  <Label htmlFor="nameA">称呼（可选）</Label>
                  <Input
                    id="nameA"
                    value={nameA}
                    onChange={(e) => setNameA(e.target.value)}
                    placeholder="如：我 / 先生 / 女士"
                    className="h-12 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* 乙方切换 */}
            <div>
              <p className="mb-2 font-serif text-lg text-fg">乙方 · 对方信息</p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setMode('pick')}
                  className={
                    'px-3 py-1 rounded text-xs border transition-colors duration-fast ' +
                    (mode === 'pick'
                      ? 'bg-accent-soft border-accent text-accent'
                      : 'border-border text-fg-2')
                  }
                >
                  从已存命盘选择
                </button>
                <button
                  type="button"
                  onClick={() => setMode('inline')}
                  className={
                    'px-3 py-1 rounded text-xs border transition-colors duration-fast ' +
                    (mode === 'inline'
                      ? 'bg-accent-soft border-accent text-accent'
                      : 'border-border text-fg-2')
                  }
                >
                  临时输入
                </button>
              </div>
              {mode === 'pick' ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <SelectField
                    label="选择命盘"
                    value={chartIdB}
                    onChange={setChartIdB}
                    placeholder="选择对方命盘"
                    options={charts.map((c) => ({
                      value: c.id,
                      label: c.displayName,
                      description: formatChartOptionDescription(c),
                    }))}
                  />
                  <div>
                    <Label htmlFor="nameBPick">称呼（可选）</Label>
                    <Input
                      id="nameBPick"
                      value={nameB}
                      onChange={(e) => setNameB(e.target.value)}
                      placeholder="如：对方 / 合伙人"
                      className="h-12 rounded-lg"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <BirthDatePicker
                      value={partnerDate}
                      onChange={setPartnerDate}
                      label="乙方出生日期（公历）"
                      required
                    />
                    <BirthTimePicker
                      value={partnerTime}
                      onChange={setPartnerTime}
                      label="乙方出生时间（可选）"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <SegmentedControl
                      label="乙方性别"
                      value={partnerGender}
                      onChange={setPartnerGender}
                      options={GENDER_OPTIONS}
                    />
                    <div>
                      <Label htmlFor="nameBInline">称呼（可选）</Label>
                      <Input
                        id="nameBInline"
                        value={nameB}
                        onChange={(e) => setNameB(e.target.value)}
                        placeholder="如：对方 / 伴侣"
                        className="h-12 rounded-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="partnerPlace">出生地（可选）</Label>
                    <Input
                      id="partnerPlace"
                      value={partnerPlace}
                      onChange={(e) => setPartnerPlace(e.target.value)}
                      placeholder="用于真太阳时校正，如：杭州"
                      className="h-12 rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}

            <div className="flex gap-3">
              {!streaming ? (
                <Button onClick={start} size="lg">
                  开始合盘分析
                </Button>
              ) : (
                <Button variant="outline" onClick={stop}>
                  中止
                </Button>
              )}
            </div>
          </Card>

          {/* 流式输出 */}
          {(streaming || content) && (
            <ReportPaper
              title="关系动力学报告"
              subtitle="从吸引、冲突、合作节奏与相处策略拆解双人关系。"
            >
              {streaming && !content ? (
                <AnalyzingOverlay />
              ) : (
                <PremiumReportRenderer
                  segments={parseReportSegments(content)}
                  streaming={streaming}
                />
              )}
              {reportId && <p className="text-xs text-fg-muted mt-6">报告 ID：{reportId}</p>}
            </ReportPaper>
          )}

          {reportId && !streaming && !content && (
            <Card className="p-6 text-sm text-fg-2">
              该历史报告尚未生成完成或没有内容。你可以重新点击“开始合盘分析”生成。
            </Card>
          )}
        </section>
      </div>
      <ReportPaywallModal
        open={paywallOpen}
        scene="compatibility.preview"
        config={paywallConfig}
        fulfillmentMeta={{ chartIdA, chartIdB: mode === 'pick' ? chartIdB : 'inline' }}
        onClose={() => setPaywallOpen(false)}
        dismissText="我再考虑一下"
      />
    </main>
  );
}

const RELATION_OPTIONS: Array<{ value: Relation; label: string; description: string }> = [
  { value: 'couple', label: '恋人 / 夫妻', description: '看吸引、冲突与长期节奏' },
  { value: 'partner', label: '商业合伙人', description: '看合作、权责与利益张力' },
  { value: 'family', label: '家人', description: '看亲缘互动与沟通模式' },
  { value: 'friend', label: '朋友', description: '看支持关系与边界感' },
];

const GENDER_OPTIONS: Array<{ value: GenderValue; label: string; description: string }> = [
  { value: 'female', label: '女', description: '按女命规则辅助判断' },
  { value: 'male', label: '男', description: '按男命规则辅助判断' },
  { value: 'unknown', label: '未指定', description: '仅做关系动力分析' },
];
